/**
 * useStageRoom — 稽古場（RT-01 / Phase 3-B・B-1）の WebSocket ＋ WebRTC を管理する
 *
 * サーバー実装（workers/anokoe/src/durable-objects/StageRoomDO.ts）のプロトコルに厳密に合わせる。
 *
 * 【接続の順序（この順を崩すと必ず落ちる）】
 *   getUserMedia → RTCPeerConnection → addTrack → createOffer/setLocalDescription
 *   → {t:'sfu-session', sdp} → {sessionId, answer} → setRemoteDescription
 *   → {t:'publish', mid} → {t:'published'}
 *   → 他者の {t:'track-available', userId} を受けたら {t:'pull', userId}
 *   → {t:'pulled', requiresImmediateRenegotiation} → 必要なら再ネゴ → ontrack
 *
 * 【ハマりどころ】
 * 1. **チケットは単回使用**。再接続のたびに必ず取り直す（使い回すと初回だけ繋がり以降401）
 * 2. **WSのURLは API_BASE から組む**。本番は Pages と Worker が別オリジンで、
 *    相対パスだとローカルのviteプロキシでは動いて本番で落ちる（SYSTEM_SPEC §7.4）
 * 3. **リモート音声の自動再生はブロックされる**。ユーザー操作を起点に play() を呼び、
 *    失敗したら「音を出す」導線を出す（無音のまま原因不明にしない）
 * 4. **ミュートは帯域を減らさない**（B-0 実測）。`enabled=false` は無音を送り続けるだけで、
 *    事実は「相手に聞こえなくなる」だけ。通信量の節約とは言わない
 *
 * 【未検証の経路（B-0 で実証できていない）】
 *   - `pull`（SFU の location:'remote'）と再ネゴシエーション。
 *     実機で落ちたら**推測を重ねず、レスポンスの status とボディをログで確かめてから**直す。
 *     そのため pull / renegotiate の応答は console に丸ごと出している。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, api, StageParticipant } from '../services/api';

/** 接続状態（5状態すべて画面に出す） */
export type StageConnState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';
export type StageError = { code: string; message: string } | null;
/** 参加の役割。**サーバーが確定する**（クライアントが送るのは希望まで） */
export type StageRole = 'performer' | 'listener';
/** 拍手を送れる間隔。サーバー側と同じ2秒（UIでも連打を抑える） */
const CLAP_INTERVAL_MS = 2000;

/** マイクが使えない理由。画面でやり直し導線を出し分ける */
export type MicError = 'denied' | 'not-found' | 'failed' | null;

const MAX_RECONNECT = 5;
/** 相手が publish し終わる前に pull すると SFU が empty_track_error を返すので、間を空けてやり直す */
const PULL_MAX_RETRY = 5;
/**
 * 「購読まわりの失敗」を表すコード。**声が届いた時点でこれらは消す**。
 * 接続そのものの失敗（WS_FAILED / TICKET_FAILED / REJECTED）は消さない ─ そちらは今も継続中の問題だから。
 */
const PULL_ERROR_CODES = new Set(['track_not_ready', 'sfu_failed', 'no_such_track', 'SDP_FAILED']);

export type RemoteAudio = { userId: string; stream: MediaStream };

/**
 * @param wantRole 入室時の**希望**。演者が満員ならサーバーが見学者に振り替える。
 *                 確定値は `myRole`（welcome で返る）を見ること。
 */
export const useStageRoom = (roomId: string, enabled: boolean, wantRole: StageRole = 'performer') => {
  const [state, setState] = useState<StageConnState>('connecting');
  const [error, setError] = useState<StageError>(null);
  const [micError, setMicError] = useState<MicError>(null);
  const [participants, setParticipants] = useState<StageParticipant[]>([]);
  const [me, setMe] = useState<StageParticipant | null>(null);
  const [closedReason, setClosedReason] = useState<string | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [publishing, setPublishing] = useState(false);
  /** サーバーが確定した自分の役割。希望どおりとは限らない（演者満員なら見学者になる） */
  const [myRole, setMyRole] = useState<StageRole>('performer');
  /** 直近2秒に届いた拍手の数。**サーバーが数えた実数**をそのまま出す */
  const [clap, setClap] = useState<{ count: number; at: number } | null>(null);
  const [remotes, setRemotes] = useState<RemoteAudio[]>([]);
  /** 自動再生がブロックされた（ユーザー操作で解除させる） */
  const [audioBlocked, setAudioBlocked] = useState(false);
  /** 誰が話しているか（音量から判定。色だけに頼らないため名前側でも強調する） */
  const [speaking, setSpeaking] = useState<Record<string, boolean>>({});

  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const senderRef = useRef<RTCRtpSender | null>(null);
  const attemptRef = useRef(0);
  const manualCloseRef = useRef(false);
  const myIdRef = useRef<string | null>(null);
  /** 確定した役割（ハンドラ内の判定は state ではなく ref を見る） */
  const myRoleRef = useRef<StageRole>('performer');
  /** 拍手を送った時刻（UI側でも連打を抑える。サーバー側でも2秒制限あり） */
  const lastClapSentRef = useRef(0);
  /**
   * 在室者の最新値。`participants` state はハンドラが張られた時点の値を掴むため、
   * WSハンドラ内の判定には ref を使う（state で判定すると welcome 直後の購読が漏れる）。
   */
  const participantsRef = useRef<StageParticipant[]>([]);
  /** すでに pull 済みの userId（track-available と welcome の両方から来るので二重防止） */
  const pulledRef = useRef<Set<string>>(new Set());
  /**
   * mid → userId。**pull の応答が返す mid で ontrack を確実に対応づける**。
   * 要求順のキューは同時に複数人が参加すると取り違えるので使わない
   * （実測で `tracks[0].mid` が返ることを確認済み・2026-08-11）。
   */
  const midToUserRef = useRef<Map<string, string>>(new Map());
  /**
   * mid → 「'pulled' より先に届いてしまった」受信ストリームの退避場所。
   * 見学者は入室時に受信枠(recvonly)を先置きするため、**最初のSFU応答の時点で
   * その枠(mid "0")の ontrack が発火する**ことがある（'pulled' の対応表登録より先）。
   * 捨てると以後は unmute しか起きず永久に無音になる（2026-08-11 実機検証で発見）。
   */
  // ストリームごと控えると再交渉時に中身が空になることがあるため、
  // transceiver（受信機）を控えて、取り付ける瞬間に receiver.track から組み立てる
  const pendingTracksRef = useRef<Map<string, { track: MediaStreamTrack; transceiver: RTCRtpTransceiver | null }>>(new Map());
  /** userId → pull のやり直し回数（無限に投げ直さない） */
  const pullRetryRef = useRef<Map<string, number>>(new Map());
  const audioCtxRef = useRef<AudioContext | null>(null);
  // Uint8Array<ArrayBuffer> と明示する（既定の ArrayBufferLike だと
  // getByteFrequencyData の引数型と食い違う）
  const analysersRef = useRef<Map<string, { analyser: AnalyserNode; data: Uint8Array<ArrayBuffer> }>>(new Map());
  const speakingTimerRef = useRef<number | null>(null);

  const send = useCallback((payload: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  // ── マイクを取る（拒否・デバイス無しを区別して伝える） ──
  const getMic = useCallback(async (): Promise<MediaStream | null> => {
    if (localStreamRef.current) return localStreamRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      localStreamRef.current = stream;
      setMicError(null);
      return stream;
    } catch (e: any) {
      const name = e?.name || '';
      setMicError(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied'
        : name === 'NotFoundError' || name === 'OverconstrainedError' ? 'not-found'
          : 'failed');
      return null;
    }
  }, []);

  /**
   * 購読まわりのエラーだけを消す。接続そのもののエラー（WS_FAILED / TICKET_FAILED 等）は残す。
   * 「一時的に失敗したが今は届いている」をエラーとして残さないための処理（事実性①）。
   */
  const clearPullError = useCallback(() => {
    setError((prev) => (prev && PULL_ERROR_CODES.has(prev.code) ? null : prev));
  }, []);

  /** 音量を見て「話している人」を出す。色だけに頼らない表示の材料 */
  const attachAnalyser = useCallback((userId: string, stream: MediaStream) => {
    try {
      const ctx = audioCtxRef.current ?? new AudioContext();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analysersRef.current.set(userId, {
        analyser,
        data: new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount)),
      });
    } catch {
      /* 解析は付加機能。失敗しても通話は続ける */
    }
  }, []);

  /** 受信トラックを参加者に取り付ける（ontrack と 'pulled' の両経路から呼ぶ） */
  const attachRemote = useCallback((userId: string, stream: MediaStream) => {
    attachAnalyser(userId, stream);
    setRemotes((prev) => (prev.some((r) => r.userId === userId) ? prev : [...prev, { userId, stream }]));
    // 【事実性①】声が実際に届いた時点で、購読まわりのエラー表示を消す。
    // 一度 pull に失敗しても再試行で回復することがあり（相手の publish 待ち）、
    // そのまま残すと「相手の声は聞こえているのに『受け取れませんでした』と書いてある」
    // という**事実と食い違う表示**になる（2026-08-11 実測）。
    pullRetryRef.current.delete(userId);
    clearPullError();
  }, [attachAnalyser, clearPullError]);

  /**
   * SFU セッションを張る。
   *
   * - **演者**: マイクを取り、sendonly のトラックを載せて offer を作る
   * - **見学者**: **`getUserMedia` を一切呼ばない**（権限ダイアログを出さないこと自体が価値）。
   *   ただし m-line が1本も無い offer は SFU が受け付けないので、
   *   `addTransceiver('audio', { direction: 'recvonly' })` で**受信専用の枠を1本**作る。
   *   ⚠️ この経路は B-0 / B-1 で通していない。落ちたら推測で直さず、
   *      下の console ログに出る**実際の応答**を見てから直すこと。
   */
  const startSession = useCallback(async (role: StageRole) => {
    let stream: MediaStream | null = null;
    if (role === 'performer') {
      stream = await getMic();
      if (!stream) return;   // マイクが取れない理由は micError に入っている
    }

    const pc = new RTCPeerConnection({ bundlePolicy: 'max-bundle' });
    pcRef.current = pc;

    // 受信したリモートトラックは **transceiver の mid** で userId に対応づける
    // （pull の応答で受け取った mid を控えてある）。
    // ⚠️ mid が未登録でも**捨てない**: 見学者の先置き受信枠は 'pulled' より先に
    //    ontrack が発火する。退避しておき、'pulled' 側で取り付け直す
    pc.ontrack = (ev) => {
      const mid = ev.transceiver?.mid ?? '';
      const stream = ev.streams[0] ?? new MediaStream([ev.track]);
      const userId = midToUserRef.current.get(mid);
      if (!userId) {
        pendingTracksRef.current.set(mid, { track: ev.track, transceiver: ev.transceiver ?? null });
        return;
      }
      attachRemote(userId, stream);
    };

    let sendTr: RTCRtpTransceiver | null = null;
    if (stream) {
      sendTr = pc.addTransceiver(stream.getAudioTracks()[0], { direction: 'sendonly' });
      senderRef.current = sendTr.sender;
    } else {
      // 見学者。送るものは無いが、**空の offer は SFU が受け付けない**ので受信枠を1本置く
      pc.addTransceiver('audio', { direction: 'recvonly' });
    }

    await pc.setLocalDescription(await pc.createOffer());
    // ⚠️ `mid` は **setLocalDescription のあと**でないと確定しない（それ以前は null）。
    //    ここより前で読むと publish が no_session で弾かれる
    if (sendTr) (pc as any).__mid = sendTr.mid;
    // ICE 収集を待つ（最大3秒。trickle しない構成に合わせる）
    await new Promise<void>((res) => {
      if (pc.iceGatheringState === 'complete') return res();
      const onChange = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', onChange); res(); } };
      pc.addEventListener('icegatheringstatechange', onChange);
      window.setTimeout(res, 3000);
    });

    send({ t: 'sfu-session', sdp: pc.localDescription?.sdp });
    // 続きは 'sfu-session' の応答で行う（演者は publish、見学者は購読だけ）
  }, [getMic, send, attachRemote]);

  // ── 接続（チケットは毎回取り直す） ──
  const connect = useCallback(async (isRetry: boolean) => {
    if (!enabled || !roomId) return;
    manualCloseRef.current = false;
    setState(isRetry ? 'reconnecting' : 'connecting');
    setError(null);

    // 役割の**希望**を送る。定員を見て確定するのはサーバー（満員なら見学者になる）
    const t = await api.stageGetTicket(roomId, wantRole);
    if (!t.success || !t.ticket) {
      setState('error');
      setError({
        code: t.code || 'TICKET_FAILED',
        message:
          t.code === 'FEATURE_DISABLED' ? '稽古場はいま準備中です'
            : t.code === 'ROOM_CLOSED' ? 'この稽古場は閉じました'
              : t.error || 'いま稽古場に入れません',
      });
      return;
    }

    // 別オリジン対策: API_BASE から ws(s):// を組む（相対パスにしない）
    const base = API_BASE || window.location.origin;
    const wsUrl = base.replace(/^http/, 'ws') + `/api/rt/stage/rooms/${roomId}/ws?ticket=${encodeURIComponent(t.ticket)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      setState('error');
      setError({ code: 'WS_FAILED', message: 'つながりませんでした' });
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setState('connected');
    };

    ws.onmessage = async (ev) => {
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }

      switch (msg.t) {
        case 'welcome': {
          myIdRef.current = msg.you?.userId ?? null;
          // **役割はサーバーが確定した値**。希望ではなくこれに表示を合わせる
          const confirmed: StageRole = msg.you?.role === 'listener' ? 'listener' : 'performer';
          myRoleRef.current = confirmed;
          setMyRole(confirmed);
          setMe(msg.you);
          participantsRef.current = msg.participants || [];
          setParticipants(msg.participants || []);
          pulledRef.current.clear();
          // 接続を張り直す以上、mid の対応表と退避トラックも作り直す
          midToUserRef.current.clear();
          pendingTracksRef.current.clear();
          // 演者はマイク→publish、見学者はマイクを使わず受信枠だけ作る
          await startSession(confirmed);
          break;
        }

        case 'sfu-session': {
          const pc = pcRef.current;
          if (!pc || !msg.answer?.sdp) break;
          try {
            await pc.setRemoteDescription({ type: 'answer', sdp: msg.answer.sdp });
            if (myRoleRef.current === 'performer') {
              send({ t: 'publish', mid: (pc as any).__mid });
            } else {
              // 見学者は publish しない。すぐ購読へ進む（演者の声を拾う）
              subscribeAllPerformers();
            }
          } catch (e) {
            console.warn('[stage] setRemoteDescription failed:', e);
            setError({ code: 'SDP_FAILED', message: '音声の接続に失敗しました' });
          }
          break;
        }

        case 'published': {
          setPublishing(true);
          subscribeAllPerformers();
          break;
        }

        case 'track-available': {
          if (msg.userId !== myIdRef.current) pull(msg.userId);
          const withTrack = (list: StageParticipant[]) =>
            list.map((p) => (p.userId === msg.userId ? { ...p, trackName: msg.trackName } : p));
          participantsRef.current = withTrack(participantsRef.current);
          setParticipants(withTrack);
          break;
        }

        case 'pulled': {
          // mid → userId を控える（ontrack の対応づけに使う）。
          // ⚠️「これより先に ontrack は来ない」は誤りだった: 見学者の先置き受信枠(mid "0")は
          //    最初のSFU応答の時点で ontrack が発火する（2026-08-11 実機検証で確認）。
          //    先に届いていた分は退避場所から取り付け直す
          if (msg.mid !== undefined && msg.mid !== null && msg.mid !== '') {
            const mid = String(msg.mid);
            midToUserRef.current.set(mid, msg.userId);
            const pending = pendingTracksRef.current.get(mid);
            if (pending) {
              pendingTracksRef.current.delete(mid);
              // 取り付ける瞬間の receiver.track を使う（退避時のストリームは再交渉で空になることがある）
              const track = pending.transceiver?.receiver?.track ?? pending.track;
              attachRemote(msg.userId, new MediaStream([track]));
            }
          }
          // 購読が受理された時点で、前回の失敗表示は事実でなくなる（再試行で回復したケース）
          pullRetryRef.current.delete(msg.userId);
          clearPullError();
          // **SFU 側が offer を出している**ので、こちらは answer を返す（自分から offer を作らない）
          if (msg.requiresImmediateRenegotiation && msg.offer?.sdp) {
            await answerSfuOffer(msg.offer.sdp);
          }
          break;
        }

        case 'renegotiated':
          if (!msg.ok) console.warn('[stage] renegotiate failed:', JSON.stringify(msg.body));
          break;

        case 'join': {
          const add = (list: StageParticipant[]) =>
            list.some((p) => p.userId === msg.participant.userId) ? list : [...list, msg.participant];
          participantsRef.current = add(participantsRef.current);
          setParticipants(add);
          break;
        }

        case 'leave':
          participantsRef.current = participantsRef.current.filter((p) => p.userId !== msg.userId);
          setParticipants((prev) => prev.filter((p) => p.userId !== msg.userId));
          setRemotes((prev) => prev.filter((r) => r.userId !== msg.userId));
          analysersRef.current.delete(msg.userId);
          pulledRef.current.delete(msg.userId);
          setSpeaking((prev) => { const n = { ...prev }; delete n[msg.userId]; return n; });
          break;

        case 'mic':
          setParticipants((prev) => prev.map((p) => (p.userId === msg.userId ? { ...p, micOn: msg.on } as any : p)));
          break;

        /**
         * 拍手。サーバーが2秒の窓で集計した**実際の回数**が来る。
         * 数字を追跡させるのが目的ではないので、UIは控えめに一瞬出すだけにする。
         */
        case 'clap':
          if (typeof msg.count === 'number' && msg.count > 0) {
            setClap({ count: msg.count, at: Date.now() });
          }
          break;

        case 'closed':
          manualCloseRef.current = true; // 閉室は再接続しない
          setClosedReason(msg.reason || 'closed');
          setState('disconnected');
          break;

        case 'error':
          /**
           * 【重要】`track_not_ready` は恒久的な失敗ではない。
           * 相手が publish を終える前に pull すると SFU が `empty_track_error` を返す
           * （HTTPは200なので気づきにくい。2026-08-11 実測）。
           * ここで諦めると**その人の声だけ永久に無音**になるので、間を空けて数回やり直す。
           */
          if (msg.code === 'track_not_ready' && msg.userId) {
            const n = (pullRetryRef.current.get(msg.userId) ?? 0) + 1;
            pullRetryRef.current.set(msg.userId, n);
            if (n <= PULL_MAX_RETRY) {
              pulledRef.current.delete(msg.userId);
              window.setTimeout(() => pull(msg.userId), 800 * n);
            } else {
              setError({ code: msg.code, message: 'その人の声を受け取れませんでした' });
            }
            break;
          }
          setError({ code: msg.code, message: msg.message || '' });
          break;

        case 'pong':
        default:
          break;
      }
    };

    ws.onerror = () => {
      if (!manualCloseRef.current) setState('error');
    };

    ws.onclose = (ev) => {
      wsRef.current = null;
      if (manualCloseRef.current) { setState('disconnected'); return; }
      // サーバーが明示的に断った(1008)場合は、取り直して投げ直しても同じ結果になるのでリトライしない
      if (ev.code === 1008) {
        setState('disconnected');
        setError((prev) => prev ?? {
          code: ev.reason || 'REJECTED',
          message: ev.reason === 'rate_limited' ? '送信が多すぎたため、いったん切断されました' : 'いま接続できませんでした',
        });
        return;
      }
      if (attemptRef.current < MAX_RECONNECT) {
        const delay = Math.min(8000, 500 * 2 ** attemptRef.current);
        attemptRef.current++;
        setState('reconnecting');
        window.setTimeout(() => { void connect(true); }, delay);
      } else {
        setState('disconnected');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, enabled]);

  /**
   * いま声が出ている**演者**をまとめて購読する。
   * state ではなく ref を見る（welcome 直後はまだ state に反映されていない）。
   */
  const subscribeAllPerformers = useCallback(() => {
    for (const p of participantsRef.current) {
      if (p.userId === myIdRef.current) continue;
      if ((p.role ?? 'performer') !== 'performer') continue; // 見学者は声を持たない
      if (p.trackName) pull(p.userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 他人の声を購読する（DO が在室者のトラックだけを解決する） */
  const pull = useCallback((userId: string) => {
    if (!userId || pulledRef.current.has(userId)) return;
    pulledRef.current.add(userId);
    send({ t: 'pull', userId });
  }, [send]);

  /**
   * 購読を追加すると SFU が offer を出す。**その answer を返す**のが再ネゴの正しい向き。
   * （自分から createOffer するのではない。2026-08-11 に実測して判明）
   */
  const answerSfuOffer = useCallback(async (offerSdp: string) => {
    const pc = pcRef.current;
    if (!pc) return;
    try {
      await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ t: 'renegotiate', sdp: pc.localDescription?.sdp });
    } catch (e) {
      console.warn('[stage] answerSfuOffer failed:', e);
      setError({ code: 'SDP_FAILED', message: '相手の声につなげませんでした' });
    }
  }, [send]);

  useEffect(() => {
    void connect(false);
    return () => {
      manualCloseRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
      pcRef.current?.close();
      pcRef.current = null;
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
      if (speakingTimerRef.current) window.clearInterval(speakingTimerRef.current);
      void audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, enabled]);

  // ── 話者判定（200msごとに音量を見る） ──
  useEffect(() => {
    speakingTimerRef.current = window.setInterval(() => {
      if (analysersRef.current.size === 0) return;
      const next: Record<string, boolean> = {};
      for (const [userId, { analyser, data }] of analysersRef.current) {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        next[userId] = sum / data.length > 12; // 経験的なしきい値
      }
      setSpeaking(next);
    }, 200);
    return () => { if (speakingTimerRef.current) window.clearInterval(speakingTimerRef.current); };
  }, []);

  /**
   * マイクのON/OFF。**帯域は減らない**（B-0 実測。enabled=false は無音を送り続ける）。
   * 事実は「相手に聞こえなくなる」だけ。
   */
  const toggleMic = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicOn(track.enabled);
    send({ t: 'mic', on: track.enabled });
  }, [send]);

  /** 自動再生がブロックされたときの手動解除。ユーザー操作から呼ぶ */
  const resumeAudio = useCallback(async () => {
    try {
      await audioCtxRef.current?.resume();
      setAudioBlocked(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  /** 再入室（チケットを取り直して張り直す） */
  const reconnect = useCallback(() => {
    attemptRef.current = 0;
    setClosedReason(null);
    void connect(true);
  }, [connect]);

  /**
   * 拍手を送る。UI側でも2秒に1回に抑える（サーバー側でも同じ制限があり、超過は黙って捨てられる）。
   * 送れたら true。連打で捨てた場合は false（画面はボタンを一時的に無効にする用途に使う）。
   */
  const sendClap = useCallback(() => {
    const t = Date.now();
    if (t - lastClapSentRef.current < CLAP_INTERVAL_MS) return false;
    lastClapSentRef.current = t;
    send({ t: 'clap' });
    return true;
  }, [send]);

  return {
    state, error, micError, participants, me, closedReason,
    micOn, publishing, remotes, speaking, audioBlocked,
    /** サーバーが確定した自分の役割 */
    myRole,
    /** 直近の拍手（サーバーが数えた実数） */
    clap,
    toggleMic, resumeAudio, reconnect, sendClap,
    retryMic: () => startSession(myRoleRef.current),
    setAudioBlocked,
  };
};
